// ─────────────────────────────────────────────────────────────────────────
// MorningUpload — Admin screen for the new two-layer Dispatch architecture.
// Angela 2026-05-11.
//
// Layer 1 (Assignment Authority): writes ONLY to `daily_assignments`.
// Layer 2 (Operational State `orders`) is NEVER touched by this screen.
//
// Pipeline:
//   1) Operator picks route_date (NY-local default).
//   2) Operator uploads REGULAR_ORDERS.xlsx + SCHEDULE.xlsx + optional GPX[].
//   3) DRY RUN preview displays the reconciliation report.
//   4) Operator clicks COMMIT to write the batch.
//   5) Audit history of recent batches is shown below.
//
// Feature-flag banner: surfaces whether USE_DAILY_ASSIGNMENTS is currently
// reading from the new authority (true) or still legacy (false).
// ─────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, MapPin, CheckCircle2, AlertTriangle,
  Loader2, RefreshCw, Eye, ShieldCheck, History, Trash2, Undo2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const nyTodayStr = () => {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
  });
  return fmt.format(new Date());
};

// ── Small inline UI atoms (kept local to avoid touching shared components) ──
const SectionCard = ({ title, icon: Icon, right, children, testid }) => (
  <div
    className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 text-[#2F5D8A]" /> : null}
        <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">{title}</h2>
      </div>
      {right}
    </div>
    {children}
  </div>
);

const StatTile = ({ label, value, tone = "neutral", testid }) => {
  const tones = {
    neutral: "bg-gray-50 border-gray-200 text-gray-700",
    good:    "bg-[#A7C4A0]/15 border-[#A7C4A0]/40 text-[#4A7A56]",
    warn:    "bg-amber-50 border-amber-200 text-amber-700",
    bad:     "bg-[#C07070]/10 border-[#C07070]/30 text-[#8B4545]",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${tones[tone]}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</p>
      <p className="text-xl font-bold mt-0.5">{value}</p>
    </div>
  );
};

const FilePill = ({ file, accept, onPick, placeholder, testid }) => (
  <label className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#2F5D8A]/40 transition-colors min-h-[44px]">
    <Upload className="h-3.5 w-3.5 text-gray-400 shrink-0" />
    <span className="text-sm text-gray-500 truncate">
      {file ? file.name : placeholder}
    </span>
    <input
      type="file"
      accept={accept}
      className="hidden"
      onChange={(e) => onPick(e.target.files?.[0] || null)}
      data-testid={testid}
    />
  </label>
);

const ListBlock = ({ rows, columns, emptyText, testid }) => {
  if (!rows || rows.length === 0) {
    return (
      <p className="text-xs text-gray-400 italic" data-testid={`${testid}-empty`}>
        {emptyText}
      </p>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] text-gray-400 uppercase tracking-wider">
            {columns.map((c) => (
              <th key={c.key} className="text-left py-1.5 pr-3 font-semibold">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-gray-100">
              {columns.map((c) => (
                <td key={c.key} className="py-1.5 pr-3 text-gray-700">{String(r[c.key] ?? "")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MorningUploadPage = ({ Layout }) => {
  const [routeDate, setRouteDate] = useState(() => nyTodayStr());
  const [regularFile, setRegularFile] = useState(null);
  const [scheduleFile, setScheduleFile] = useState(null);
  const [gpxFiles, setGpxFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);   // { report, dry_run: true }
  const [lastCommit, setLastCommit] = useState(null);
  const [flag, setFlag] = useState({ use_daily_assignments: false, today_ny: "" });
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  // Rollback-duplicate state (Angela 2026-05-11)
  const [rollbackBusy, setRollbackBusy] = useState(false);
  const [rollbackPreview, setRollbackPreview] = useState(null);  // dry_run response
  const [rollbackApplied, setRollbackApplied] = useState(null);  // apply response

  const fetchFlag = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dispatch/morning-upload/feature-flag`);
      setFlag(r.data || { use_daily_assignments: false });
    } catch (e) { /* ignore */ }
  }, []);

  // Angela 2026-05-15 — Step 4: history capped at last 7 uploads.
  const fetchBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const r = await axios.get(`${API}/dispatch/morning-upload/batches?limit=7`);
      setBatches(r.data?.batches || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  // Angela 2026-05-15 — Step 3: per-batch Undo (rollback by batch id).
  // Honours `daily_closed=yes` protection on the backend.
  const [batchRollbackBusyId, setBatchRollbackBusyId] = useState(null);
  const [batchRollbackPreview, setBatchRollbackPreview] = useState(null); // dry-run response
  const [batchRollbackApplied, setBatchRollbackApplied] = useState(null);

  const ageHours = (createdIso) => {
    if (!createdIso) return Infinity;
    const t = Date.parse(createdIso);
    if (Number.isNaN(t)) return Infinity;
    return (Date.now() - t) / 3600000;
  };

  const canUndoBatch = (b) => {
    if (!b) return false;
    if (b.rolled_back === true) return false;
    if (b.status === "rolled_back") return false;
    if (b.status !== "committed") return false;
    if (ageHours(b.committed_at || b.created_at) > 24) return false;
    return true;
  };

  const handleBatchUndoDryRun = async (batchId) => {
    setBatchRollbackBusyId(batchId);
    setBatchRollbackApplied(null);
    try {
      const r = await axios.post(
        `${API}/dispatch/morning-upload/rollback/${batchId}`,
        { dry_run: true }
      );
      setBatchRollbackPreview({ ...r.data, upload_batch_id: batchId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback preview failed.");
    } finally {
      setBatchRollbackBusyId(null);
    }
  };

  const handleBatchUndoApply = async () => {
    if (!batchRollbackPreview) return;
    const batchId = batchRollbackPreview.upload_batch_id;
    if (!window.confirm(
      `Roll back batch ${batchId.slice(0, 8)}?\n\n` +
      `Route date: ${batchRollbackPreview.batch_route_date}\n` +
      `Rows to deactivate: ${batchRollbackPreview.rows_to_deactivate}\n` +
      `Rows protected by daily_closed=yes: ${batchRollbackPreview.rows_protected_by_daily_closed}\n\n` +
      `Soft-deactivate only — daily_closed orders STAY active. ` +
      `orders/Finance/Scheduling/SMS/QA are NEVER touched.`
    )) return;
    setBatchRollbackBusyId(batchId);
    try {
      const r = await axios.post(
        `${API}/dispatch/morning-upload/rollback/${batchId}`,
        { dry_run: false }
      );
      setBatchRollbackApplied({ ...r.data, upload_batch_id: batchId });
      setBatchRollbackPreview(null);
      toast.success(
        `Batch ${batchId.slice(0, 8)} rolled back — ` +
        `${r.data.rows_to_deactivate} rows deactivated, ` +
        `${r.data.rows_protected_by_daily_closed} protected.`
      );
      fetchBatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed.");
    } finally {
      setBatchRollbackBusyId(null);
    }
  };

  useEffect(() => { fetchFlag(); fetchBatches(); }, [fetchFlag, fetchBatches]);

  // ── Rollback last duplicate batch (Admin/Dispatch/Operations only) ──
  const handleRollbackDryRun = async () => {
    setRollbackBusy(true);
    setRollbackApplied(null);
    try {
      const r = await axios.post(`${API}/dispatch/morning-upload/rollback-duplicate`, { dry_run: true });
      if (r.data?.status === "no_duplicates") {
        toast.info("No duplicate batch detected in the last 5 uploads.");
        setRollbackPreview(null);
      } else {
        setRollbackPreview(r.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to inspect duplicates.");
    } finally {
      setRollbackBusy(false);
    }
  };
  const handleRollbackApply = async () => {
    if (!rollbackPreview) return;
    if (!window.confirm(
      `Rollback duplicate batch?\n\n` +
      `Target batch: ${rollbackPreview.target_batch_id?.slice(0, 8)}…\n` +
      `Route date  : ${rollbackPreview.target_route_date}\n` +
      `Rows to deactivate: ${rollbackPreview.rows_to_deactivate}\n` +
      `Per-inspector impact: ${JSON.stringify(rollbackPreview.per_inspector_impact)}\n\n` +
      `Soft-deactivate only (active=false + audit fields). ` +
      `orders/Daily Close/Finance/Scheduling/SMS/QA NEVER touched. ` +
      `First valid batch stays active.`
    )) return;
    setRollbackBusy(true);
    try {
      const r = await axios.post(`${API}/dispatch/morning-upload/rollback-duplicate`, { dry_run: false });
      setRollbackApplied(r.data);
      setRollbackPreview(null);
      toast.success(`Rolled back ${r.data.deactivated_rows} rows from batch ${r.data.target_batch_id?.slice(0, 8)}…`);
      fetchBatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed.");
    } finally {
      setRollbackBusy(false);
    }
  };

  const canSubmit = !!(routeDate && regularFile && scheduleFile);

  const buildFormData = (dryRun) => {
    const fd = new FormData();
    fd.append("regular_file", regularFile);
    fd.append("schedule_file", scheduleFile);
    fd.append("route_date", routeDate);
    fd.append("dry_run", dryRun ? "true" : "false");
    (gpxFiles || []).forEach((g) => {
      if (g) fd.append("gpx_files", g);
    });
    return fd;
  };

  const handleDryRun = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setLastCommit(null);
    try {
      const res = await axios.post(`${API}/dispatch/morning-upload`, buildFormData(true), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
      toast.success("Dry run complete — review the report below before committing.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Dry run failed";
      toast.error(typeof msg === "string" ? msg : "Dry run failed");
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    if (!canSubmit) return;
    if (!preview) {
      toast.error("Run DRY RUN first so you can review the reconciliation report.");
      return;
    }
    // Angela 2026-05-15 — Hard-conflict pre-check. Backend will return 409
    // anyway, but blocking client-side is faster + clearer.
    const xb = preview.report.totals?.cross_batch_hard_conflicts || 0;
    const gpx = preview.report.totals?.gpx_hard_conflicts || 0;
    if (xb > 0 || gpx > 0) {
      toast.error(
        `Cannot commit — ${xb} cross-batch hard conflict(s) and ${gpx} GPX inspector ` +
        `mismatch(es) detected. Cancel and re-upload corrected files.`
      );
      return;
    }
    if (!window.confirm(
      `Commit Morning Upload for ${routeDate}?\n\n` +
      `Planned assignments: ${preview.report.totals.planned_assignments}\n` +
      `Pseudo-skipped (AARIFI-*): ${preview.report.totals.pseudo_skipped}\n` +
      `Invalid inspector: ${preview.report.totals.invalid_inspector}\n` +
      `Not found in orders: ${preview.report.totals.not_found_in_orders}\n\n` +
      `This will write to daily_assignments. The orders collection is NOT touched.`
    )) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/dispatch/morning-upload`, buildFormData(false), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastCommit(res.data);
      setPreview(null);
      toast.success(`Committed batch ${res.data.upload_batch_id?.slice(0, 8)} — ${res.data.report?.totals?.planned_assignments || 0} assignments written.`);
      fetchBatches();
    } catch (e) {
      // Angela 2026-05-15 — Surface 409 safety-check blocks with the report
      // so the operator sees exactly why and can cancel + re-upload.
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 409 && data?.status === "blocked_by_safety_checks") {
        // Re-display the safety-check report in the preview pane so the
        // operator can review the conflict detail without re-running dry-run.
        setPreview({ report: data.report, dry_run: true, blocked: true });
        toast.error(data.blocked_reason || "Commit blocked by safety checks.");
      } else {
        const msg = data?.detail || data?.blocked_reason || e?.message || "Commit failed";
        toast.error(typeof msg === "string" ? msg : "Commit failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const report = lastCommit?.report || preview?.report || null;
  const isDryRunPreview = !!preview && !lastCommit;
  const totals = report?.totals || {};
  const perInspector = report?.per_inspector_counts || {};

  // Angela 2026-05-15 — Hard-conflict gate for the Commit button. Uses
  // the new safety-check counts surfaced by the backend (Step 1).
  const crossBatchHardConflicts = totals.cross_batch_hard_conflicts || 0;
  const gpxHardConflicts = totals.gpx_hard_conflicts || 0;
  const hasHardConflicts =
    isDryRunPreview && (crossBatchHardConflicts > 0 || gpxHardConflicts > 0);
  const crossBatchRows = report?.cross_batch_duplicates || [];
  const gpxMismatchRows = report?.gpx_inspector_mismatch || [];

  const inspectorChips = useMemo(() =>
    Object.entries(perInspector).sort(([, a], [, b]) => b - a),
    [perInspector]
  );

  const body = (
    <div
      className={Layout ? "max-w-6xl mx-auto p-4 sm:p-6 space-y-5" : "space-y-5"}
      data-testid="morning-upload-page"
    >

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-[#2F5D8A]" />
            Morning Dispatch Upload
          </h1>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            Two-layer architecture: this screen writes the day's <b>assignment authority</b> to
            <code className="px-1 py-0.5 bg-gray-100 rounded mx-1">daily_assignments</code>.
            The <code className="px-1 py-0.5 bg-gray-100 rounded mx-1">orders</code> collection is never touched here.
          </p>
        </div>
        <div
          className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider border ${
            flag.use_daily_assignments
              ? "bg-[#A7C4A0]/15 border-[#A7C4A0]/40 text-[#4A7A56]"
              : "bg-amber-50 border-amber-200 text-amber-700"
          }`}
          data-testid="feature-flag-badge"
        >
          {flag.use_daily_assignments ? "Live reads ON" : "Live reads OFF (staging mode)"}
        </div>
      </div>

      {/* Upload form */}
      <SectionCard
        title="1 · Choose route date and files"
        icon={FileSpreadsheet}
        testid="upload-form-card"
      >
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">
              Route Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={routeDate}
              onChange={(e) => setRouteDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-[#2F5D8A]"
              data-testid="route-date-input"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">
              REGULAR_ORDERS.xlsx <span className="text-red-500">*</span>
            </label>
            <FilePill
              file={regularFile}
              accept=".xlsx"
              onPick={setRegularFile}
              placeholder="Choose REGULAR_ORDERS.xlsx"
              testid="regular-file-input"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">
              SCHEDULE.xlsx <span className="text-red-500">*</span>
            </label>
            <FilePill
              file={scheduleFile}
              accept=".xlsx"
              onPick={setScheduleFile}
              placeholder="Choose SCHEDULE.xlsx"
              testid="schedule-file-input"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">
              GPX routes (optional)
            </label>
            <label className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#2F5D8A]/40 transition-colors min-h-[44px]">
              <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              <span className="text-sm text-gray-500 truncate">
                {gpxFiles?.length
                  ? `${gpxFiles.length} GPX file(s) selected`
                  : "Choose one or more .gpx files"}
              </span>
              <input
                type="file"
                accept=".gpx,.xml"
                multiple
                className="hidden"
                onChange={(e) => setGpxFiles(Array.from(e.target.files || []))}
                data-testid="gpx-files-input"
              />
            </label>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-5">
          <button
            onClick={handleDryRun}
            disabled={!canSubmit || busy}
            className="px-4 py-2.5 bg-gray-700 hover:bg-gray-800 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-40 flex items-center gap-2"
            data-testid="dry-run-btn"
          >
            {busy && isDryRunPreview === false ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
            Dry run preview
          </button>
          <button
            onClick={handleCommit}
            disabled={!canSubmit || busy || !preview || hasHardConflicts}
            className="px-4 py-2.5 bg-[#2F5D8A] hover:bg-[#264A6E] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-40 flex items-center gap-2"
            data-testid="commit-btn"
            title={
              !preview
                ? "Run dry run first"
                : hasHardConflicts
                  ? "Resolve cross-batch / GPX conflicts before committing"
                  : "Commit batch"
            }
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Commit batch
          </button>
          {(regularFile || scheduleFile || gpxFiles.length > 0 || preview) && (
            <button
              onClick={() => { setRegularFile(null); setScheduleFile(null); setGpxFiles([]); setPreview(null); }}
              className="px-3 py-2 text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1.5"
              data-testid="reset-btn"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Reset
            </button>
          )}
        </div>
      </SectionCard>

      {/* Reconciliation Report */}
      {report && (
        <SectionCard
          title={isDryRunPreview ? "2 · Reconciliation preview (DRY RUN)" : "2 · Commit result"}
          icon={isDryRunPreview ? Eye : CheckCircle2}
          testid="report-card"
          right={
            <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider ${
              isDryRunPreview ? "bg-amber-100 text-amber-700" : "bg-[#A7C4A0]/20 text-[#4A7A56]"
            }`}>
              {isDryRunPreview ? "Preview only — no writes" : `Committed · ${lastCommit?.upload_batch_id?.slice(0, 8)}`}
            </span>
          }
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            <StatTile label="Planned" value={totals.planned_assignments ?? 0} tone="good" testid="stat-planned" />
            <StatTile label="REGULAR rows" value={totals.regular_rows ?? 0} testid="stat-regular" />
            <StatTile label="SCHEDULE rows" value={totals.schedule_rows ?? 0} testid="stat-schedule" />
            <StatTile label="GPX confirmed" value={`${totals.gpx_confirmed ?? 0}/${totals.gpx_waypoints ?? 0}`} testid="stat-gpx" />
            <StatTile label="Pseudo skipped" value={totals.pseudo_skipped ?? 0} tone={totals.pseudo_skipped ? "warn" : "neutral"} testid="stat-pseudo" />
            <StatTile label="Invalid inspector" value={totals.invalid_inspector ?? 0} tone={totals.invalid_inspector ? "bad" : "neutral"} testid="stat-invalid" />
            <StatTile label="Duplicates" value={totals.duplicate_orders ?? 0} tone={totals.duplicate_orders ? "warn" : "neutral"} testid="stat-dup" />
            <StatTile label="Not found in orders" value={totals.not_found_in_orders ?? 0} tone={totals.not_found_in_orders ? "warn" : "neutral"} testid="stat-notfound" />
            <StatTile label="Missing in GPX" value={totals.missing_in_gpx ?? 0} testid="stat-missing-gpx" />
            <StatTile label="GPX extras" value={totals.gpx_extras ?? 0} tone={totals.gpx_extras ? "warn" : "neutral"} testid="stat-gpx-extras" />
            <StatTile label="GPX parse errors" value={totals.gpx_parse_errors ?? 0} tone={totals.gpx_parse_errors ? "warn" : "neutral"} testid="stat-gpx-errors" />
            <StatTile label="GPX no inspector hint" value={totals.gpx_no_inspector_hint ?? 0} tone={totals.gpx_no_inspector_hint ? "warn" : "neutral"} testid="stat-gpx-no-hint" />
            <StatTile label="GPX persisted to My Map" value={totals.gpx_persisted ?? 0} tone={(totals.gpx_persisted ?? 0) > 0 ? "good" : "neutral"} testid="stat-gpx-persisted" />
            <StatTile label="Missing inspector" value={totals.missing_inspector ?? 0} tone={totals.missing_inspector ? "warn" : "neutral"} testid="stat-missing-insp" />
            {/* Angela 2026-05-15 — new safety-check tiles. */}
            <StatTile
              label="Cross-batch duplicates"
              value={totals.cross_batch_duplicates ?? 0}
              tone={(totals.cross_batch_hard_conflicts ?? 0) > 0 ? "bad" : (totals.cross_batch_duplicates ?? 0) > 0 ? "warn" : "neutral"}
              testid="stat-cross-batch"
            />
            <StatTile
              label="GPX inspector mismatch"
              value={totals.gpx_inspector_mismatch_files ?? 0}
              tone={(totals.gpx_hard_conflicts ?? 0) > 0 ? "bad" : (totals.gpx_inspector_mismatch_files ?? 0) > 0 ? "warn" : "neutral"}
              testid="stat-gpx-mismatch"
            />
          </div>

          {/* Angela 2026-05-15 — Hard-conflict alert banner. Only shown on
              DRY RUN previews when the backend would block the commit. */}
          {hasHardConflicts && (
            <div
              className="mb-5 p-4 rounded-lg border-2 border-[#C07070]/50 bg-[#C07070]/5"
              data-testid="hard-conflict-banner"
            >
              <div className="flex items-start gap-2.5">
                <AlertTriangle className="h-5 w-5 text-[#C07070] mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-bold text-[#8B3F3F] mb-1">
                    Commit blocked — safety checks failed
                  </p>
                  <p className="text-[#8B3F3F]/90 leading-relaxed">
                    {crossBatchHardConflicts > 0 && (
                      <span>
                        <b>{crossBatchHardConflicts}</b> order(s) are already
                        assigned to a different inspector in <b>daily_assignments</b>{" "}
                        for {routeDate}.
                      </span>
                    )}
                    {crossBatchHardConflicts > 0 && gpxHardConflicts > 0 && <br />}
                    {gpxHardConflicts > 0 && (
                      <span>
                        <b>{gpxHardConflicts}</b> GPX file(s) have an inspector
                        hint that does not match the XLSX assignments.
                      </span>
                    )}
                    <br />
                    <span className="text-xs">
                      Click <b>Reset</b>, fix the upload files, and run dry-run
                      again. No data has been written.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Per-inspector chips */}
          <div className="mb-5">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Per-inspector counts ({inspectorChips.length})
            </p>
            <div className="flex flex-wrap gap-1.5">
              {inspectorChips.length === 0 ? (
                <span className="text-xs text-gray-400 italic">No whitelisted inspectors in this batch.</span>
              ) : inspectorChips.map(([code, n]) => (
                <span
                  key={code}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#2F5D8A]/10 text-[#2F5D8A] border border-[#2F5D8A]/20"
                  data-testid={`per-insp-${code}`}
                >
                  {code}
                  <span className="px-1.5 py-0.5 rounded bg-white text-[#2F5D8A]">{n}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Buckets */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div>
              <h3 className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                Pseudo skipped <span className="text-gray-400 font-normal">({report.pseudo_skipped?.length || 0})</span>
              </h3>
              <ListBlock
                rows={report.pseudo_skipped || []}
                columns={[
                  { key: "source", label: "Source" },
                  { key: "row", label: "Row" },
                  { key: "order_number", label: "Order #" },
                  { key: "pseudo_value", label: "Pseudo value" },
                ]}
                emptyText="None."
                testid="bucket-pseudo"
              />
            </div>
            <div>
              <h3 className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-[#C07070]" />
                Invalid inspector <span className="text-gray-400 font-normal">({report.invalid_inspector?.length || 0})</span>
              </h3>
              <ListBlock
                rows={report.invalid_inspector || []}
                columns={[
                  { key: "source", label: "Source" },
                  { key: "row", label: "Row" },
                  { key: "order_number", label: "Order #" },
                  { key: "inspector", label: "Inspector" },
                ]}
                emptyText="None."
                testid="bucket-invalid"
              />
            </div>
            <div>
              <h3 className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                Duplicate orders <span className="text-gray-400 font-normal">({report.duplicate_orders?.length || 0})</span>
              </h3>
              <ListBlock
                rows={report.duplicate_orders || []}
                columns={[
                  { key: "order_number", label: "Order #" },
                  { key: "kept_source", label: "Kept" },
                  { key: "kept_inspector", label: "Kept insp" },
                  { key: "ignored_source", label: "Ignored" },
                  { key: "ignored_inspector", label: "Ignored insp" },
                ]}
                emptyText="None."
                testid="bucket-dup"
              />
            </div>
            <div>
              <h3 className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                Not found in orders <span className="text-gray-400 font-normal">({report.not_found_in_orders?.length || 0})</span>
              </h3>
              <ListBlock
                rows={report.not_found_in_orders || []}
                columns={[
                  { key: "order_number", label: "Order #" },
                  { key: "inspector_code", label: "Inspector" },
                  { key: "source", label: "Source" },
                ]}
                emptyText="None."
                testid="bucket-notfound"
              />
            </div>
            <div>
              <h3 className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-amber-500" />
                Missing in GPX <span className="text-gray-400 font-normal">({report.missing_in_gpx?.length || 0})</span>
              </h3>
              <ListBlock
                rows={report.missing_in_gpx || []}
                columns={[
                  { key: "order_number", label: "Order #" },
                  { key: "inspector_code", label: "Inspector" },
                  { key: "source", label: "Source" },
                ]}
                emptyText="None — every planned order has a GPX waypoint."
                testid="bucket-missing-gpx"
              />
            </div>
            <div>
              <h3 className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-[#C07070]" />
                GPX extras <span className="text-gray-400 font-normal">({report.gpx_extras?.length || 0})</span>
              </h3>
              <ListBlock
                rows={report.gpx_extras || []}
                columns={[
                  { key: "order_number", label: "Order #" },
                  { key: "file", label: "File" },
                  { key: "inspector_hint", label: "Inspector hint" },
                  { key: "sequence", label: "Seq" },
                ]}
                emptyText="None — every GPX waypoint matched a planned order."
                testid="bucket-gpx-extras"
              />
            </div>
            <div>
              <h3 className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                GPX no inspector hint <span className="text-gray-400 font-normal">({report.gpx_no_inspector_hint?.length || 0})</span>
              </h3>
              <ListBlock
                rows={report.gpx_no_inspector_hint || []}
                columns={[
                  { key: "file", label: "File" },
                  { key: "waypoints", label: "Waypoints" },
                  { key: "reason", label: "Reason" },
                ]}
                emptyText="None — every GPX filename carried a valid inspector code."
                testid="bucket-gpx-no-hint"
              />
              {(report.gpx_no_inspector_hint?.length || 0) > 0 && (
                <p className="text-[10px] mt-2 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  ⚠ Rename these files like <code>route_MA-LUCASOLIVEIRA.gpx</code> and re-upload — the office must encode the inspector code in the filename so My Map can pick up the route. The file was NOT persisted.
                </p>
              )}
            </div>
            <div>
              <h3 className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-[#4A7A56]" />
                GPX persisted to My Map <span className="text-gray-400 font-normal">({report.gpx_persisted?.length || 0})</span>
              </h3>
              <ListBlock
                rows={report.gpx_persisted || []}
                columns={[
                  { key: "inspector_code", label: "Inspector" },
                  { key: "filename", label: "File" },
                  { key: "waypoints", label: "Waypoints" },
                ]}
                emptyText="None — no GPX file produced a persisted route (yet)."
                testid="bucket-gpx-persisted"
              />
            </div>

            {/* Angela 2026-05-15 — Cross-batch duplicate detail list. */}
            <div>
              <h3 className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5">
                <AlertTriangle className={`h-3.5 w-3.5 ${crossBatchHardConflicts > 0 ? "text-[#C07070]" : "text-amber-500"}`} />
                Cross-batch duplicates{" "}
                <span className="text-gray-400 font-normal">({crossBatchRows.length})</span>
                {crossBatchHardConflicts > 0 && (
                  <span
                    className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-[#8B3F3F] bg-[#C07070]/10 border border-[#C07070]/30 rounded px-1.5 py-0.5"
                    data-testid="cross-batch-hard-badge"
                  >
                    {crossBatchHardConflicts} hard
                  </span>
                )}
              </h3>
              <ListBlock
                rows={crossBatchRows}
                columns={[
                  { key: "order_number", label: "Order #" },
                  { key: "existing_inspector", label: "Existing insp" },
                  { key: "attempted_inspector", label: "Attempted insp" },
                  { key: "kind", label: "Kind" },
                  { key: "existing_source", label: "Existing src" },
                ]}
                emptyText="None — no order today already lives under another batch."
                testid="bucket-cross-batch"
              />
            </div>

            {/* Angela 2026-05-15 — GPX inspector mismatch detail list. */}
            <div>
              <h3 className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5">
                <MapPin className={`h-3.5 w-3.5 ${gpxHardConflicts > 0 ? "text-[#C07070]" : "text-amber-500"}`} />
                GPX inspector mismatch{" "}
                <span className="text-gray-400 font-normal">({gpxMismatchRows.length})</span>
                {gpxHardConflicts > 0 && (
                  <span
                    className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-[#8B3F3F] bg-[#C07070]/10 border border-[#C07070]/30 rounded px-1.5 py-0.5"
                    data-testid="gpx-mismatch-hard-badge"
                  >
                    {gpxHardConflicts} hard
                  </span>
                )}
              </h3>
              <ListBlock
                rows={gpxMismatchRows.map(m => ({
                  file: m.file,
                  gpx_inspector_hint: m.gpx_inspector_hint,
                  waypoints: m.waypoints,
                  xlsx_has_any_for_hint: m.xlsx_has_any_for_hint ? "yes" : "NO",
                  mismatch_count: m.mismatch_count,
                }))}
                columns={[
                  { key: "file", label: "File" },
                  { key: "gpx_inspector_hint", label: "GPX hint" },
                  { key: "waypoints", label: "Waypoints" },
                  { key: "xlsx_has_any_for_hint", label: "XLSX has hint?" },
                  { key: "mismatch_count", label: "Mismatched" },
                ]}
                emptyText="None — every GPX matches the XLSX-assigned inspector."
                testid="bucket-gpx-mismatch"
              />
            </div>
          </div>
        </SectionCard>
      )}

      {/* Recent batches */}
      <SectionCard
        title="3 · Recent batches"
        icon={History}
        testid="batches-card"
        right={
          <div className="flex items-center gap-3">
            <button
              onClick={handleRollbackDryRun}
              disabled={rollbackBusy}
              className="text-xs text-[#8B4545] hover:text-[#6E3535] flex items-center gap-1 disabled:opacity-40"
              data-testid="rollback-dry-run-btn"
              title="Inspect the last 5 batches and flag any duplicate for rollback"
            >
              {rollbackBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
              Rollback last duplicate
            </button>
            <button
              onClick={fetchBatches}
              className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
              data-testid="batches-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loadingBatches ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        }
      >
        {/* Rollback preview / applied panels (Angela 2026-05-11) */}
        {rollbackPreview && (
          <div className="mb-4 p-3 rounded-lg border border-[#C07070]/30 bg-[#C07070]/5" data-testid="rollback-preview-panel">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-[#8B4545] uppercase tracking-wider">
                Duplicate detected — DRY RUN preview
              </p>
              <button
                onClick={() => setRollbackPreview(null)}
                className="text-[10px] text-gray-500 hover:text-gray-800"
                data-testid="rollback-cancel-btn"
              >Cancel</button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
              <div><span className="text-gray-400">Target batch (latest):</span> <code className="text-[10px]">{rollbackPreview.target_batch_id}</code></div>
              <div><span className="text-gray-400">Kept batch (first):</span> <code className="text-[10px]">{rollbackPreview.kept_batch_id}</code></div>
              <div><span className="text-gray-400">Route date:</span> <strong>{rollbackPreview.target_route_date}</strong></div>
              <div><span className="text-gray-400">Uploaded by:</span> {rollbackPreview.target_uploaded_by}</div>
              <div><span className="text-gray-400">Rows to deactivate:</span> <strong>{rollbackPreview.rows_to_deactivate}</strong></div>
              <div><span className="text-gray-400">Per-inspector:</span> <code className="text-[10px]">{JSON.stringify(rollbackPreview.per_inspector_impact)}</code></div>
            </div>
            <p className="text-[10px] text-gray-500 mt-2">
              Soft-deactivate only (<code>active=false</code> + audit fields). The orders collection, Daily Close, Finance, Scheduling, SMS and QA are NEVER touched. The first valid batch stays active.
            </p>
            <button
              onClick={handleRollbackApply}
              disabled={rollbackBusy}
              className="mt-3 px-4 py-2 bg-[#8B4545] hover:bg-[#6E3535] text-white text-xs font-semibold rounded-lg flex items-center gap-2 disabled:opacity-40"
              data-testid="rollback-apply-btn"
            >
              {rollbackBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
              Confirm rollback — soft-deactivate {rollbackPreview.rows_to_deactivate} rows
            </button>
          </div>
        )}
        {rollbackApplied && (
          <div className="mb-4 p-3 rounded-lg border border-[#A7C4A0]/40 bg-[#A7C4A0]/15" data-testid="rollback-applied-panel">
            <p className="text-xs font-bold text-[#4A7A56] uppercase tracking-wider mb-2">
              Rollback applied
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
              <div><span className="text-gray-400">Target batch:</span> <code className="text-[10px]">{rollbackApplied.target_batch_id}</code></div>
              <div><span className="text-gray-400">Deactivated rows:</span> <strong>{rollbackApplied.deactivated_rows}</strong></div>
              <div className="col-span-2"><span className="text-gray-400">Corrected counts:</span> <code className="text-[10px]">{JSON.stringify(rollbackApplied.post_per_inspector_counts)}</code></div>
            </div>
          </div>
        )}
        {/* Angela 2026-05-15 — Step 3: per-batch Undo preview/applied panels. */}
        {batchRollbackPreview && (
          <div
            className="mb-4 p-3 rounded-lg border border-[#C07070]/40 bg-[#C07070]/5"
            data-testid="batch-rollback-preview-panel"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-[#8B4545] uppercase tracking-wider">
                Undo batch — DRY RUN preview
              </p>
              <button
                onClick={() => setBatchRollbackPreview(null)}
                className="text-[10px] text-gray-500 hover:text-gray-800"
                data-testid="batch-rollback-cancel-btn"
              >Cancel</button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
              <div>
                <span className="text-gray-400">Batch:</span>{" "}
                <code className="text-[10px]">{batchRollbackPreview.upload_batch_id}</code>
              </div>
              <div>
                <span className="text-gray-400">Route date:</span>{" "}
                <strong>{batchRollbackPreview.batch_route_date}</strong>
              </div>
              <div>
                <span className="text-gray-400">Total rows in batch:</span>{" "}
                {batchRollbackPreview.total_rows_in_batch}
              </div>
              <div>
                <span className="text-gray-400">Age:</span>{" "}
                {batchRollbackPreview.age_hours}h
              </div>
              <div>
                <span className="text-gray-400">Rows to deactivate:</span>{" "}
                <strong className="text-[#8B4545]">{batchRollbackPreview.rows_to_deactivate}</strong>
              </div>
              <div>
                <span className="text-gray-400">Protected (daily_closed):</span>{" "}
                <strong className="text-[#4A7A56]">{batchRollbackPreview.rows_protected_by_daily_closed}</strong>
              </div>
              <div>
                <span className="text-gray-400">GPX routes to supersede:</span>{" "}
                {batchRollbackPreview.gpx_routes_to_supersede}
              </div>
              <div>
                <span className="text-gray-400">Per-inspector impact:</span>{" "}
                <code className="text-[10px]">
                  {JSON.stringify(batchRollbackPreview.per_inspector_to_deactivate || {})}
                </code>
              </div>
            </div>
            {batchRollbackPreview.protected_orders?.length > 0 && (
              <div className="mt-2 p-2 rounded bg-[#A7C4A0]/10 border border-[#A7C4A0]/30">
                <p className="text-[10px] font-bold text-[#4A7A56] uppercase tracking-wider mb-1">
                  Protected orders ({batchRollbackPreview.protected_orders.length}) — will STAY active
                </p>
                <div className="flex flex-wrap gap-1">
                  {batchRollbackPreview.protected_orders.slice(0, 30).map((o) => (
                    <span
                      key={o.order_number}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-[#A7C4A0]/40 text-[#4A7A56]"
                      data-testid={`protected-order-${o.order_number}`}
                    >
                      {o.order_number}
                    </span>
                  ))}
                  {batchRollbackPreview.protected_orders.length > 30 && (
                    <span className="text-[10px] text-gray-500 italic">
                      +{batchRollbackPreview.protected_orders.length - 30} more…
                    </span>
                  )}
                </div>
              </div>
            )}
            <p className="text-[10px] text-gray-500 mt-2">
              Soft-deactivate only. Orders marked <code>daily_closed=yes</code>,{" "}
              <code>qa_status=Approved</code>, or <code>transmitted=yes</code> stay
              ACTIVE. Finance / Scheduling / SMS / QA are NEVER touched.
            </p>
            <button
              onClick={handleBatchUndoApply}
              disabled={batchRollbackBusyId !== null}
              className="mt-3 px-4 py-2 bg-[#8B4545] hover:bg-[#6E3535] text-white text-xs font-semibold rounded-lg flex items-center gap-2 disabled:opacity-40"
              data-testid="batch-rollback-apply-btn"
            >
              {batchRollbackBusyId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
              Confirm undo — soft-deactivate {batchRollbackPreview.rows_to_deactivate} rows
            </button>
          </div>
        )}
        {batchRollbackApplied && (
          <div
            className="mb-4 p-3 rounded-lg border border-[#A7C4A0]/40 bg-[#A7C4A0]/15"
            data-testid="batch-rollback-applied-panel"
          >
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-bold text-[#4A7A56] uppercase tracking-wider">
                Batch rolled back
              </p>
              <button
                onClick={() => setBatchRollbackApplied(null)}
                className="text-[10px] text-gray-500 hover:text-gray-800"
                data-testid="batch-rollback-applied-dismiss"
              >Dismiss</button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
              <div>
                <span className="text-gray-400">Batch:</span>{" "}
                <code className="text-[10px]">{batchRollbackApplied.upload_batch_id}</code>
              </div>
              <div>
                <span className="text-gray-400">Route date:</span>{" "}
                <strong>{batchRollbackApplied.batch_route_date}</strong>
              </div>
              <div>
                <span className="text-gray-400">Rows deactivated:</span>{" "}
                <strong>{batchRollbackApplied.rows_to_deactivate}</strong>
              </div>
              <div>
                <span className="text-gray-400">Protected (kept active):</span>{" "}
                <strong>{batchRollbackApplied.rows_protected_by_daily_closed}</strong>
              </div>
            </div>
          </div>
        )}
        {/* batches table follows */}
        {batches.length === 0 ? (
          <p className="text-xs text-gray-400 italic" data-testid="batches-empty">
            No morning uploads yet for the active environment.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] text-gray-400 uppercase tracking-wider">
                  <th className="text-left py-1.5 pr-3 font-semibold">Created</th>
                  <th className="text-left py-1.5 pr-3 font-semibold">Route date</th>
                  <th className="text-left py-1.5 pr-3 font-semibold">Status</th>
                  <th className="text-left py-1.5 pr-3 font-semibold">By</th>
                  <th className="text-left py-1.5 pr-3 font-semibold">Planned</th>
                  <th className="text-left py-1.5 pr-3 font-semibold">Pseudo skipped</th>
                  <th className="text-left py-1.5 pr-3 font-semibold">GPX confirmed</th>
                  <th className="text-left py-1.5 pr-3 font-semibold">Batch id</th>
                  <th className="text-left py-1.5 pr-3 font-semibold">Undo</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => {
                  // Angela 2026-05-15 — Step 4: derive a clearer pill label.
                  const rolledBack = b.rolled_back === true || b.status === "rolled_back";
                  const hardConflicts =
                    (b.report?.totals?.cross_batch_hard_conflicts || 0) +
                    (b.report?.totals?.gpx_hard_conflicts || 0);
                  let pillLabel = b.status;
                  let pillTone = "bg-amber-50 text-amber-700";
                  if (rolledBack) {
                    pillLabel = "Rolled back";
                    pillTone = "bg-[#C07070]/10 text-[#8B4545]";
                  } else if (b.status === "committed" && hardConflicts > 0) {
                    pillLabel = "Conflicts";
                    pillTone = "bg-amber-100 text-amber-800";
                  } else if (b.status === "committed") {
                    pillLabel = "Success";
                    pillTone = "bg-[#A7C4A0]/15 text-[#4A7A56]";
                  }
                  const undoOk = canUndoBatch(b);
                  const undoBusy = batchRollbackBusyId === b.upload_batch_id;
                  let undoTitle = "Undo this batch";
                  if (rolledBack) undoTitle = "Already rolled back";
                  else if (b.status !== "committed") undoTitle = "Batch not committed";
                  else if (ageHours(b.committed_at || b.created_at) > 24)
                    undoTitle = "Older than 24h — admin escalation required";
                  return (
                    <tr key={b.upload_batch_id} className="border-t border-gray-100" data-testid={`batch-row-${b.upload_batch_id}`}>
                      <td className="py-1.5 pr-3 text-gray-700">{(b.created_at || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="py-1.5 pr-3 font-semibold text-gray-800">{b.route_date}</td>
                      <td className="py-1.5 pr-3">
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${pillTone}`}
                          data-testid={`batch-status-${b.upload_batch_id}`}
                        >{pillLabel}</span>
                      </td>
                      <td className="py-1.5 pr-3 text-gray-700">{b.uploaded_by_name || b.uploaded_by_email}</td>
                      <td className="py-1.5 pr-3 text-gray-700">{b.report?.totals?.planned_assignments ?? 0}</td>
                      <td className="py-1.5 pr-3 text-gray-700">{b.report?.totals?.pseudo_skipped ?? 0}</td>
                      <td className="py-1.5 pr-3 text-gray-700">
                        {b.report?.totals?.gpx_confirmed ?? 0}/{b.report?.totals?.gpx_waypoints ?? 0}
                      </td>
                      <td className="py-1.5 pr-3 text-gray-400 font-mono">{b.upload_batch_id.slice(0, 8)}</td>
                      <td className="py-1.5 pr-3">
                        <button
                          onClick={() => handleBatchUndoDryRun(b.upload_batch_id)}
                          disabled={!undoOk || undoBusy}
                          title={undoTitle}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border border-[#C07070]/30 text-[#8B4545] hover:bg-[#C07070]/10 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`batch-undo-btn-${b.upload_batch_id}`}
                        >
                          {undoBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
                          Undo
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );

  return Layout ? <Layout>{body}</Layout> : body;
};

export default MorningUploadPage;
